import { useCallback, useMemo, useRef } from 'react';
import { LastMinuteAutoConfirm } from '~/constants/ff.posthog';
import { fireShiftRequestLastMinuteAutoconfirmExperimentExposed } from '~/posthogs/events';
import { isShiftWithinLastMinuteWindow } from '~/utils/singleShiftTiming';
import { useFeatureFlagVariant } from './useFeatureFlagVariant';
import { usePaymentEligibility } from './usePaymentEligibility';
import { usePosthog } from './usePosthog';
import { useSelectedOffice } from './useSelectedOffice';

/**
 * Per-day arrival times. Only that one field decides the window, so this stays a partial of
 * `IShiftOverride` — callers weighing an edit in progress can pass the pending time without
 * having to synthesise a whole override.
 */
export type TArrivalTimeOverrides = Record<string, { arrivalTime?: string } | undefined>;

export interface IUseLastMinuteAutoConfirmResult {
  /** Every shift in the posting starts inside the last-minute window. */
  isLastMinute: boolean;
  /** Office is bucketed into the experiment's `test` variant. */
  isTestVariant: boolean;
  /** Auto-Confirm must be on for this posting and the office cannot turn it off. */
  shouldForceOn: boolean;
  /** Fires the experiment exposure event once, and only for last-minute postings. */
  exposeExperiment: () => void;
}

/**
 * Every shift in the posting starts inside the window — one shift outside takes the whole
 * posting out of the experiment. Each day is resolved against its own overridden arrival time
 * when it has one, matching how `buildJobPayloads` builds each shift.
 */
const isEveryShiftWithinWindow = (
  dates: Date[] | undefined,
  arrivalTime: string | undefined,
  shiftOverrides: TArrivalTimeOverrides | undefined,
  timeZone: string | undefined,
): boolean => {
  if (!dates?.length || !arrivalTime) return false;

  return dates.every((date) => {
    const override = shiftOverrides?.[date.toISOString()];
    return isShiftWithinLastMinuteWindow(date, override?.arrivalTime ?? arrivalTime, timeZone);
  });
};

/**
 * Drives the last-minute Auto-Confirm experiment (GOTU-5391) on the shift request form: the
 * `test` variant forces both Auto-Confirm toggles on and locks them (GOTU-5399/5398).
 *
 * Exposure is qualified on `isLastMinute` and payment eligibility, so offices that were never
 * candidates for the treatment don't dilute the control group.
 *
 * Applies when EVERY shift in the posting starts inside the window; one shift outside takes the
 * whole posting out (confirmed with Jason, 18 Aug 2026).
 */
export const useLastMinuteAutoConfirm = (
  officeId?: string,
  dates?: Date[],
  arrivalTime?: string,
  shiftOverrides?: TArrivalTimeOverrides,
): IUseLastMinuteAutoConfirmResult => {
  const posthog = usePosthog();
  const isTestVariant = useFeatureFlagVariant(LastMinuteAutoConfirm, 'test');

  // Must be the office the SHIFT is for, not the one selected in the sidebar: a DSO parent can
  // post into a child office in another timezone, and reading the sidebar office's zone would
  // judge the window against the wrong clock.
  const timeZone: string | undefined = useSelectedOffice(officeId)?.time_zone;

  const isLastMinute = useMemo(
    () => isEveryShiftWithinWindow(dates, arrivalTime, shiftOverrides, timeZone),
    [dates, arrivalTime, shiftOverrides, timeZone],
  );

  // Auto-Confirm requires a payment method, so an office without one is never forced on
  // (GOTU-7076). `isEligible` reads false while the query is in flight, which is the safe
  // default: the lock arrives a beat late rather than forcing on before we know.
  const { isEligible } = usePaymentEligibility(officeId);

  const shouldForceOn = isTestVariant && isLastMinute && isEligible;

  const exposedRef = useRef(false);
  const exposeExperiment = useCallback(() => {
    // Also qualified on eligibility, matching `useAutoConfirmExperiment`: now that an office
    // without a payment method can never receive the treatment, exposing it would only dilute
    // the control group with offices that were never candidates (GOTU-7076).
    if (exposedRef.current || !isLastMinute || !isEligible) return;
    exposedRef.current = true;

    const variant = posthog?.getFeatureFlagVariant(LastMinuteAutoConfirm);
    fireShiftRequestLastMinuteAutoconfirmExperimentExposed({
      flagKey: LastMinuteAutoConfirm,
      variant: typeof variant === 'string' ? variant : String(variant),
    });
  }, [isLastMinute, isEligible, posthog]);

  return {
    isLastMinute,
    isTestVariant,
    shouldForceOn,
    exposeExperiment,
  };
};
